/**
 * ScanModelAlignment — Phase 2.
 *
 * Workflow:
 *   1. Pick a scan session + design model
 *   2. Create an alignment session (auto-codes ALN-001…)
 *   3. Pick corresponding points in the scan + model viewers
 *      (click directly on the point cloud to capture Scan XYZ; click on the
 *      Forge model to capture Model XYZ). Manual XYZ entry remains available
 *      as a fallback.
 *   4. Compute via SVD → server returns transform + RMS residual
 *   5. Accept → writes transform to designModel.alignmentTransform
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import PointCloudViewer from '@/components/scan3d/PointCloudViewer';
import ForgeViewerWrapper from '@/components/scan3d/ForgeViewerWrapper';
import {
  Plus, Trash2, Loader2, Check, X, ArrowRight, Crosshair, Sigma, RefreshCw, MousePointerClick,
} from 'lucide-react';

const empty = () => ({ id: `PP-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, label: '',
  scanPoint: { x: 0, y: 0, z: 0 }, modelPoint: { x: 0, y: 0, z: 0 }, residualMm: 0 });

export default function ScanModelAlignment({ projectId }) {
  const [scanSessionId, setScanSessionId] = useState('');
  const [scans, setScans] = useState([]);
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [session, setSession] = useState(null);
  const [pts, setPts] = useState([empty(), empty(), empty()]);
  const [transform, setTransform] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  // Click-to-pick state: row index + side that will receive the next click
  const [pickTarget, setPickTarget] = useState({ rowIdx: 0, side: 'scanPoint' });
  const forgeRef = useRef(null);
  const forgeContainerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    try {
      const [s, m, ss] = await Promise.all([
        api.get(`/v1/projects/${projectId}/scan3d/sessions?limit=50`),
        api.get(`/v1/projects/${projectId}/scan3d/models?isLatest=true`),
        api.get(`/v1/projects/${projectId}/scan3d/alignment/sessions`),
      ]);
      setScans(s.data?.sessions || s.data?.items || []);
      setModels((m.data?.models || []).filter(x => x.translationStatus === 'SUCCESS' || x.translationStatus === 'TRANSLATING'));
      setSessions(ss.data?.sessions || []);
    } catch (_) { /* ignore */ }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createSession = async () => {
    if (!scanSessionId || !modelId) { toast.error('Pick a scan + model'); return; }
    try {
      const r = await api.post(`/v1/projects/${projectId}/scan3d/alignment/sessions`, {
        scanSessionId, designModelId: modelId, method: 'MANUAL_PICK_POINTS',
      });
      setSession(r.data);
      setPts([empty(), empty(), empty()]);
      setTransform(null);
      refresh();
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const compute = async () => {
    if (!session) return;
    if (pts.length < 3) { toast.error('Need ≥3 pick-point pairs'); return; }
    setBusy(true);
    try {
      // Persist points then compute
      await api.post(`/v1/projects/${projectId}/scan3d/alignment/sessions/${session.id}/pick-points`, {
        pickPoints: pts.map(p => ({ id: p.id, label: p.label, scanPoint: p.scanPoint, modelPoint: p.modelPoint })),
      });
      const r = await api.post(`/v1/projects/${projectId}/scan3d/alignment/sessions/${session.id}/compute`);
      setTransform(r.data?.transform);
      setPts(r.data?.pickPoints || pts);
      toast.success(`Computed — RMS ${r.data?.transform?.rmsResidualMm?.toFixed(3)}mm`);
    } catch (e) {
      toast.error(`Compute failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const refineICP = async () => {
    if (!session || !transform) return;
    setBusy(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/scan3d/alignment/sessions/${session.id}/auto-icp`);
      setTransform(r.data?.transform);
      toast.success('ICP refinement returned — see note in transform.note');
    } catch (e) { toast.error('ICP refine failed'); }
    finally { setBusy(false); }
  };

  const accept = async () => {
    if (!session || !transform) return;
    try {
      await api.post(`/v1/projects/${projectId}/scan3d/alignment/sessions/${session.id}/accept`);
      toast.success('Alignment accepted — written to the design model');
      setSession({ ...session, status: 'ACCEPTED' });
      refresh();
    } catch { toast.error('Accept failed'); }
  };

  const setRow = (i, side, axis, v) => {
    setPts(prev => prev.map((p, idx) => idx === i ? {
      ...p,
      [side]: { ...p[side], [axis]: parseFloat(v) || 0 },
    } : p));
  };
  const setLabel = (i, v) => setPts(prev => prev.map((p, idx) => idx === i ? { ...p, label: v } : p));
  const addRow = () => setPts(prev => [...prev, empty()]);
  const delRow = (i) => setPts(prev => prev.filter((_, idx) => idx !== i));

  // Set an entire XYZ at once (used by click-to-pick)
  const setPoint = useCallback((rowIdx, side, xyz) => {
    setPts(prev => prev.map((p, idx) => idx === rowIdx ? {
      ...p,
      [side]: { x: +xyz.x, y: +xyz.y, z: +xyz.z },
    } : p));
  }, []);

  // Click handler for the scan point cloud — fires when user clicks a point
  const onPcPick = useCallback((world) => {
    if (pickTarget.side !== 'scanPoint') {
      toast.message('Currently picking Model side — switch to Scan in the pick bar.');
      return;
    }
    setPoint(pickTarget.rowIdx, 'scanPoint', world);
    toast.success(`Row ${pickTarget.rowIdx + 1} · Scan picked (${world.x.toFixed(2)}, ${world.y.toFixed(2)}, ${world.z.toFixed(2)})`);
    // Auto-advance to model side, so the next click captures the matching pair
    setPickTarget(t => ({ ...t, side: 'modelPoint' }));
  }, [pickTarget, setPoint]);

  // Resolve the scan API URL once a session is selected
  const scanApiUrl = useMemo(() => {
    if (!scanSessionId || !projectId) return null;
    const sess = scans.find(s => (s.id || s.sessionCode) === scanSessionId);
    const rawFile = (sess?.rawFiles || [])[0];
    const fileId = rawFile?.fileId || rawFile?.id || rawFile?._id;
    if (!fileId) return null;
    const sid = sess.id || sess.sessionCode;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/v1/projects/${projectId}/scan3d/sessions/${sid}/files/${fileId}/points-bin?limit=2000000`;
  }, [scanSessionId, projectId, scans]);

  const selectedModel = useMemo(() => models.find(m => m.id === modelId), [models, modelId]);

  // Click handler installed on the Forge container — uses viewer.hitTest to
  // convert (clientX, clientY) → world XYZ on the design model surface.
  useEffect(() => {
    const container = forgeContainerRef.current;
    if (!container) return;
    const onClick = (e) => {
      const viewer = forgeRef.current;
      if (!viewer) return;
      if (pickTarget.side !== 'modelPoint') {
        toast.message('Currently picking Scan side — switch to Model in the pick bar.');
        return;
      }
      try {
        const hit = viewer.hitTest(e.clientX, e.clientY);
        if (!hit || !hit.intersectPoint) {
          toast.message('No surface hit — click on a model face.');
          return;
        }
        const p = hit.intersectPoint;
        setPoint(pickTarget.rowIdx, 'modelPoint', { x: p.x, y: p.y, z: p.z });
        toast.success(`Row ${pickTarget.rowIdx + 1} · Model picked (${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)})`);
        // Auto-advance to next row's scan side
        setPickTarget(t => ({ rowIdx: t.rowIdx + 1 < pts.length ? t.rowIdx + 1 : t.rowIdx, side: 'scanPoint' }));
      } catch (err) {
        // Forge hitTest can throw if a frame is mid-render or the model hasn't loaded yet.
        // We swallow but log — this aids future debugging without breaking the UX.
        console.warn('Forge hitTest failed:', err);
      }
    };
    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }, [pickTarget, pts.length, setPoint]);

  return (
    <div className="space-y-4" data-testid="scan3d-alignment-tab">
      {/* Step 1 */}
      <div className="rounded-xl p-4" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
        <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Step 1 · Pick Scan + Design Model</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <label className="text-xs">
            <span className="opacity-70">Scan session</span>
            <select value={scanSessionId} onChange={e => setScanSessionId(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
              data-testid="alignment-scan-select">
              <option value="">— select —</option>
              {scans.map(s => <option key={s.id || s.sessionCode} value={s.id || s.sessionCode}>{s.sessionCode} · {s.area || s.purpose}</option>)}
            </select>
          </label>
          <label className="text-xs">
            <span className="opacity-70">Design model</span>
            <select value={modelId} onChange={e => setModelId(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
              data-testid="alignment-model-select">
              <option value="">— select —</option>
              {models.map(m => <option key={m.id} value={m.id}>{m.modelCode} · {m.modelName} v{m.versionNumber}</option>)}
            </select>
          </label>
          <button onClick={createSession} disabled={!scanSessionId || !modelId}
            className="px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40"
            style={{ background: '#3b82f6', color: '#fff' }}
            data-testid="alignment-create-session-btn">
            Create alignment session
          </button>
        </div>
      </div>

      {/* Active session */}
      {session && (
        <div className="rounded-xl p-4 space-y-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }} data-testid="alignment-active-session">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60">Active session</div>
              <div className="text-base font-bold">{session.sessionCode}</div>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{
                background: session.status === 'ACCEPTED' ? '#10b98122' : '#3b82f622',
                color: session.status === 'ACCEPTED' ? '#10b981' : '#3b82f6',
              }}>{session.status}</span>
          </div>

          <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Step 2 · Pick-Point Pairs (≥3)</div>

          {/* Pick-target bar */}
          <div className="rounded-lg p-2 flex items-center gap-2 text-[11px]" style={{ background: '#0a0e14', border: '1px solid #1f2937' }} data-testid="alignment-pick-bar">
            <MousePointerClick className="w-3 h-3 opacity-60" />
            <span className="opacity-60">Next click captures</span>
            <span className="font-semibold" style={{ color: '#fbbf24' }}>Row {pickTarget.rowIdx + 1}</span>
            <span className="opacity-60">·</span>
            <button
              onClick={() => setPickTarget(t => ({ ...t, side: 'scanPoint' }))}
              className="px-2 py-0.5 rounded font-semibold"
              style={{
                background: pickTarget.side === 'scanPoint' ? '#3b82f6' : '#0f1722',
                color: pickTarget.side === 'scanPoint' ? '#fff' : '#9ca3af',
                border: '1px solid #1f2937',
              }}
              data-testid="alignment-pick-side-scan">
              Scan
            </button>
            <button
              onClick={() => setPickTarget(t => ({ ...t, side: 'modelPoint' }))}
              className="px-2 py-0.5 rounded font-semibold"
              style={{
                background: pickTarget.side === 'modelPoint' ? '#3b82f6' : '#0f1722',
                color: pickTarget.side === 'modelPoint' ? '#fff' : '#9ca3af',
                border: '1px solid #1f2937',
              }}
              data-testid="alignment-pick-side-model">
              Model
            </button>
            <div className="ml-auto flex items-center gap-1 opacity-60">
              <span className="opacity-60">Row</span>
              <select value={pickTarget.rowIdx}
                onChange={e => setPickTarget(t => ({ ...t, rowIdx: parseInt(e.target.value, 10) }))}
                className="px-1 py-0.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                data-testid="alignment-pick-row-select">
                {pts.map((_, i) => <option key={i} value={i}>{i + 1}</option>)}
              </select>
            </div>
          </div>

          {/* Dual viewers — left scan / right model */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="alignment-viewer-pair">
            <div className="rounded-xl overflow-hidden" style={{ background: '#0a0e14', border: pickTarget.side === 'scanPoint' ? '1px solid #fbbf24' : '1px solid #1f2937' }}>
              {scanApiUrl ? (
                <PointCloudViewer apiUrl={scanApiUrl} height={320} onPick={onPcPick} />
              ) : (
                <div className="h-[320px] flex items-center justify-center text-xs opacity-60">No raw scan file on this session</div>
              )}
            </div>
            <div ref={forgeContainerRef} className="rounded-xl overflow-hidden" style={{ border: pickTarget.side === 'modelPoint' ? '1px solid #fbbf24' : '1px solid #1f2937' }}>
              {selectedModel?.svf2Urn || selectedModel?.apsUrn ? (
                <ForgeViewerWrapper projectId={projectId} urn={selectedModel.svf2Urn || selectedModel.apsUrn}
                  onReady={(v) => { forgeRef.current = v; }} height={320}
                  testId="alignment-forge-viewer" />
              ) : (
                <div className="h-[320px] flex items-center justify-center text-xs opacity-60" style={{ background: '#0a0e14' }}>
                  Selected model has no SVF2 URN yet — wait for translation
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead style={{ background: '#0a0e14' }}>
                <tr>
                  <th className="px-2 py-1 text-left">#</th>
                  <th className="px-2 py-1 text-left">Label</th>
                  <th className="px-2 py-1 text-left">Scan X</th>
                  <th className="px-2 py-1 text-left">Scan Y</th>
                  <th className="px-2 py-1 text-left">Scan Z</th>
                  <th></th>
                  <th className="px-2 py-1 text-left">Model X</th>
                  <th className="px-2 py-1 text-left">Model Y</th>
                  <th className="px-2 py-1 text-left">Model Z</th>
                  <th className="px-2 py-1 text-left">Residual</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pts.map((p, i) => (
                  <tr key={p.id} data-testid={`alignment-pick-row-${i}`}>
                    <td className="px-2 py-1 opacity-60">{i + 1}</td>
                    <td className="px-2 py-1">
                      <input value={p.label} onChange={e => setLabel(i, e.target.value)} placeholder="Col A1 base"
                        className="w-24 px-1 py-0.5 rounded text-xs" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
                    </td>
                    {['x', 'y', 'z'].map(ax => (
                      <td key={'s' + ax} className="px-2 py-1">
                        <input type="number" step="any" value={p.scanPoint[ax]}
                          onChange={e => setRow(i, 'scanPoint', ax, e.target.value)}
                          className="w-16 px-1 py-0.5 rounded text-xs"
                          style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
                      </td>
                    ))}
                    <td><ArrowRight className="w-3 h-3 opacity-40" /></td>
                    {['x', 'y', 'z'].map(ax => (
                      <td key={'m' + ax} className="px-2 py-1">
                        <input type="number" step="any" value={p.modelPoint[ax]}
                          onChange={e => setRow(i, 'modelPoint', ax, e.target.value)}
                          className="w-16 px-1 py-0.5 rounded text-xs"
                          style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
                      </td>
                    ))}
                    <td className="px-2 py-1 text-xs" style={{ color: p.residualMm > 5 ? '#ef4444' : p.residualMm > 1 ? '#f59e0b' : '#10b981' }}>
                      {p.residualMm ? p.residualMm.toFixed(2) + 'mm' : '—'}
                    </td>
                    <td className="px-2 py-1">
                      <button onClick={() => delRow(i)} disabled={pts.length <= 3} className="p-0.5 rounded hover:bg-white/10 disabled:opacity-30"
                        title="Remove">
                        <Trash2 className="w-3 h-3 opacity-70" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addRow} className="flex items-center gap-1 px-2 py-1 rounded text-xs"
              style={{ background: '#0a0e14', color: '#9ca3af', border: '1px solid #1f2937' }}>
              <Plus className="w-3 h-3" /> Add pair
            </button>
            <button onClick={compute} disabled={busy || pts.length < 3}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40"
              style={{ background: '#3b82f6', color: '#fff' }}
              data-testid="alignment-compute-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sigma className="w-3 h-3" />} Compute (SVD)
            </button>
            {transform && (
              <button onClick={refineICP} disabled={busy}
                className="flex items-center gap-1 px-2 py-1.5 rounded text-xs"
                style={{ background: '#0a0e14', color: '#9ca3af', border: '1px solid #1f2937' }}>
                <RefreshCw className="w-3 h-3" /> Refine with ICP
              </button>
            )}
          </div>

          {/* Transform readout */}
          {transform && (
            <div className="rounded-lg p-3" style={{ background: '#0a0e14', border: '1px solid #1f2937' }} data-testid="alignment-transform-card">
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2 flex items-center gap-1"><Crosshair className="w-3 h-3" /> Computed transform</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                <KV k="ΔX" v={`${transform.translation.x.toFixed(2)}mm`} />
                <KV k="ΔY" v={`${transform.translation.y.toFixed(2)}mm`} />
                <KV k="ΔZ" v={`${transform.translation.z.toFixed(2)}mm`} />
                <KV k="RMS" v={`${transform.rmsResidualMm.toFixed(3)}mm`} color="#10b981" />
                <KV k="Max" v={`${transform.maxResidualMm.toFixed(3)}mm`} color={transform.maxResidualMm > 5 ? '#ef4444' : '#10b981'} />
                <KV k="Rx°" v={transform.rotation.x.toFixed(4)} />
                <KV k="Ry°" v={transform.rotation.y.toFixed(4)} />
                <KV k="Rz°" v={transform.rotation.z.toFixed(4)} />
                <KV k="Scale" v={transform.scale.toFixed(5)} />
              </div>
              {transform.note && <div className="text-[10px] opacity-50 mt-2 italic">{transform.note}</div>}
              <div className="flex gap-2 mt-3">
                <button onClick={accept} disabled={session.status === 'ACCEPTED'}
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40"
                  style={{ background: '#10b981', color: '#fff' }}
                  data-testid="alignment-accept-btn">
                  <Check className="w-3 h-3" /> Accept & write to model
                </button>
                <button onClick={() => api.post(`/v1/projects/${projectId}/scan3d/alignment/sessions/${session.id}/reject`).then(() => { setSession({ ...session, status: 'REJECTED' }); toast.info('Rejected'); })}
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-xs"
                  style={{ background: '#0a0e14', color: '#ef4444', border: '1px solid #ef444433' }}>
                  <X className="w-3 h-3" /> Reject & retry
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Past sessions */}
      {sessions.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
          <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Past alignment sessions</div>
          <table className="w-full text-xs">
            <thead style={{ background: '#0a0e14' }}>
              <tr><th className="text-left px-2 py-1">Code</th><th className="text-left px-2 py-1">Scan</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">RMS</th><th className="text-left px-2 py-1">Created</th></tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s.id}>
                  <td className="px-2 py-1 font-mono">{s.sessionCode}</td>
                  <td className="px-2 py-1 opacity-80">{s.scanSessionId}</td>
                  <td className="px-2 py-1">
                    <span className="px-2 py-0.5 rounded-full text-[10px]"
                      style={{ background: s.status === 'ACCEPTED' ? '#10b98122' : '#3b82f622', color: s.status === 'ACCEPTED' ? '#10b981' : '#3b82f6' }}>{s.status}</span>
                  </td>
                  <td className="px-2 py-1">{s.computedTransform?.rmsResidualMm?.toFixed(3) ?? '—'} mm</td>
                  <td className="px-2 py-1 opacity-60">{s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KV({ k, v, color }) {
  return (
    <div className="rounded p-1.5" style={{ background: '#111827' }}>
      <div className="opacity-50 text-[9px] uppercase">{k}</div>
      <div className="font-semibold font-mono" style={color ? { color } : null}>{v}</div>
    </div>
  );
}
